import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { useEffect, useCallback, useMemo } from "react";
import { GET_PRODUCT_VARIANTS } from "@/pages/events/queries";
import Select from "@/components/Select";
import { FormValues } from "../validation/schema";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { useHasEventDefaults } from "../FormVisibilityContext";

interface Variant {
  id: string;
  name: string;
  priceCents: string;
  externalId?: string;
}

interface VariantOption extends Variant {
  value: string;
  displayText: string;
}

interface VariantsDropdownProps {
  index: number;
  productId: string;
  disabled?: boolean;
}

const selectVariantBySessionYear = (sessionDate: string, variants: any[]) => {
  const sessionYear = new Date(sessionDate).getFullYear();

  const variantSelected = variants.find((variant) => variant.externalId?.endsWith(`PAT-Registration-${sessionYear}`));
  return variantSelected || variants.find((variant) => variant.externalId?.endsWith(`PAT-Registration`));
};

const VariantsDropdown = ({ index, productId, disabled }: VariantsDropdownProps) => {
  const {
    control,
    watch,
    clearErrors,
    trigger,
    setValue,
    formState: { errors }
  } = useFormContext<FormValues>();

  const sessions = watch("sessions");
  const sessionDate = sessions?.[0]?.date?.startDate;
  const hasEventDefaults = useHasEventDefaults();
  const setVariant = useCallback(
    (variant: VariantOption) => {
      setValue(`products.${index}.variantId`, variant.id);
      setValue(`products.${index}.variantName`, variant.name);
      setValue(`products.${index}.variantPrice`, parseInt(variant.priceCents));
    },
    [setValue, index]
  );

  const { data: variantData } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { productId },
    onCompleted: (data) => {
      const variants = data?.commerceProductVariants?.nodes || [];
      const currentVariantId = watch(`products.${index}.variantId`);

      if (variants.length > 0 && !currentVariantId) {
        const selectedVariant = selectVariantBySessionYear(sessionDate, variants);
        setVariant(selectedVariant);
        trigger(`products.${index}.variantId`);
      }
    }
  });

  const variantOptions = useMemo(
    () =>
      variantData?.commerceProductVariants.nodes.map(
        (variant: Variant): VariantOption => ({
          id: variant.id,
          value: variant.id,
          displayText: centsToDollarsFormatted(parseInt(variant.priceCents)) ?? "",
          name: variant.name,
          priceCents: variant.priceCents,
          externalId: variant.externalId
        })
      ) || [],
    [variantData?.commerceProductVariants.nodes]
  );

  useEffect(() => {
    if (variantOptions.length > 0 && sessionDate) {
      const selectedVariant = selectVariantBySessionYear(sessionDate, variantOptions);
      const currentVariantId = watch(`products.${index}.variantId`);
      if (currentVariantId !== selectedVariant?.id && !hasEventDefaults) {
        setVariant(selectedVariant);
        trigger(`products.${index}.variantId`);
      }
    }
  }, [sessionDate, variantOptions, index, watch, setValue, trigger, setVariant, hasEventDefaults]);

  return (
    <Controller
      name={`products.${index}.variantId`}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Select
          label="Price"
          options={variantOptions}
          className="w-full"
          value={value}
          disabled={disabled}
          onSelect={(value) => {
            onChange(value);
            const selectedVariant = variantOptions.find((option: VariantOption) => option.value === value);
            setVariant(selectedVariant || variantOptions[0]);

            if (value && errors.products?.[index]?.variantId) {
              clearErrors(`products.${index}.variantId`);
            }
            if (!value) {
              trigger(`products.${index}.variantId`);
            }
          }}
          required
          data-testid={`variant-select-${index}`}
          error={!!errors.products?.[index]?.variantId}
          errorText={errors.products?.[index]?.variantId?.message as string}
        />
      )}
    />
  );
};

export default VariantsDropdown;
